/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { faFileAlt, faInfoCircle, faPlus, faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Col, Collapse, Container, OverlayTrigger, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  findSectionByCodeAndSurvey,
  generateSectionCode,
  getSectionNameByCodeAndSurvey,
  getSurveySectionCodes,
} from '../../actions/helpers/section.helper';
import benTooltip from '../../Components/BenTooltip';
import { ONBOARDING_STEPS, QUESTION_FILTER_TYPE } from '../../constants/common';
import { getFilterSampleId } from '../../helpers/filters';
import types from '../../types';

import FilterEditForm from './components/FilterEditForm';
import FilterSample404Dialog from './components/FilterEditForm/filterSample404Dialog';
import OtherFilters from './components/OtherFilters';
import QuestionEditForm from './components/QuestionEditForm';
import AttributeQuestionDialog from './components/QuestionEditForm/attributeQuestionDialog';
import SampleDimension404Dialog from './components/QuestionEditForm/sampleDimension404Dialog';
import SectionEditForm from './components/SectionEditForm';
import SectionQuestions from './components/SectionQuestions';
import SurveyInformationCard from './components/SurveyInformationCard';
import SurveyInformationEditForm from './components/SurveyInformationEditForm';
import useFilterEditor from './hooks/useFilterEditor';
import useOnboarding from './hooks/useOnboarding';
import useQuestionEditor from './hooks/useQuestionEditor';
import useSectionQuestions from './hooks/useSectionQuestions';
import useSurveyData from './hooks/useSurveyData';
import useWizardNav, { WIZARD_AREAS } from './hooks/useWizardNav';
import recodeSectionQuestions from './utils/recodeSectionQuestions';
import saveSurveySection from './utils/saveSurveySection';
import './styles.css';

// The backend clears query caches asynchronously after a save; give it a
// moment before refetching the preview data (same grace the old wizard used).
const PREVIEW_REFRESH_DELAY_MS = 2000;

const SurveyEditWizard = () => {
  const dispatch = useDispatch();
  const { id: surveyId } = useParams();

  const nav = useWizardNav();
  const data = useSurveyData(surveyId);
  const sectionQuestions = useSectionQuestions(surveyId, data.jwt);
  const editor = useQuestionEditor(data.jwt);
  const filterEditor = useFilterEditor(data.jwt);
  // Which attribute filter is expanded on the Other Filters panel (and loaded
  // into the filter edit pane).
  const [selectedFilter, setSelectedFilter] = useState(null);
  // Live tag state (order/hidden/text) of the filter being edited, mirrored
  // from FilterEditForm so the overview's expanded row follows the edits.
  const [filterTagPreview, setFilterTagPreview] = useState(null);
  const { isStepVisible, completeStep } = useOnboarding({ load: true });
  const [questionRefreshToken, setQuestionRefreshToken] = useState(0);
  // True from the moment a question save lands until the preview has been
  // refetched from the backend, so the loading overlay shows continuously
  // instead of flashing once for the save and again for the reload.
  const [previewRefreshing, setPreviewRefreshing] = useState(false);
  // Saving flags for the two sidebar forms, so each can block its own inputs
  // while its request is in flight.
  const [savingSurveyInfo, setSavingSurveyInfo] = useState(false);
  const [savingSection, setSavingSection] = useState(false);
  // Shown in the section form when a save fails or only partly succeeds.
  const [sectionSaveError, setSectionSaveError] = useState('');

  // Signals from shared components that stay redux-connected:
  // MoveQuestionToSection announces "move to section" / reorder through these.
  const { section: sectionToMove, questionId: sampleToMoveId } = useSelector(
    (state) => state.sections
  );
  const { sampleMoved, loading: sampleActionLoading } = useSelector((state) => state.surveySample);
  const { openedQuestions } = useSelector((state) => state.surveyDetails);
  const openedQuestionsRef = useRef(openedQuestions);
  openedQuestionsRef.current = openedQuestions;
  // Which question panel to reopen once a save has finished refreshing.
  const reopenQuestionIdRef = useRef(null);
  // The in-flight loadQuestion of the last clicked question.
  const questionLoadRef = useRef(null);
  // Which clicked question is still opening, and whether one is: true from the
  // click until its sample, chart detail and categories have all loaded, so
  // neither panel shows partial data in between.
  const openingQuestionIdRef = useRef(null);
  const [openingQuestion, setOpeningQuestion] = useState(false);

  const { currentSurvey, allSections } = data;

  const handleSelectSection = useCallback(
    (sectionCode, sectionName) => {
      const sectionData = findSectionByCodeAndSurvey(sectionCode, currentSurvey, allSections);
      if (sectionData) {
        // Keep the survey's bare section code on the selection: that is what
        // the questions endpoint and the name lookup expect, while the entry's
        // own code may already be the composite form.
        nav.selectSection({
          ...sectionData,
          // The entry's code as stored, so a save can tell whether this
          // survey owns the entry or shares a legacy one.
          storedCode: sectionData.Data.code,
          Data: { ...sectionData.Data, code: sectionCode },
        });
      } else {
        if (sectionCode === '') {
          nav.openEditor(WIZARD_AREAS.SECTIONS);
          sectionQuestions.clear();
        }
        nav.selectSection({
          Id: -1,
          Data: {
            code: sectionCode,
            name: sectionName,
          },
        });
      }
    },
    [allSections, currentSurvey, nav, sectionQuestions]
  );

  // "New Section": reserve a code no other section of this survey uses and
  // open the section form. Nothing is persisted until the form is saved.
  const handleNewSection = useCallback(() => {
    setSectionSaveError('');
    sectionQuestions.clear();
    nav.selectSection({
      Id: -1,
      isNew: true,
      Data: {
        code: generateSectionCode(currentSurvey, allSections),
        name: '',
      },
    });
    nav.openEditor(WIZARD_AREAS.SECTIONS);
  }, [allSections, currentSurvey, nav, sectionQuestions]);

  // Leaving the form of a section that was never saved discards it.
  const handleCloseSectionEditor = useCallback(() => {
    setSectionSaveError('');
    if (nav.selectedSection?.isNew) nav.selectSection(null);
    nav.closeEditor();
  }, [nav]);

  // Selected section changed -> fetch its questions. A section that is still
  // being created has none to fetch.
  useEffect(() => {
    if (
      nav.selectedSection &&
      !nav.selectedSection.isNew &&
      (nav.selectedSection.Id !== -1 || nav.selectedSection.Data.code !== '')
    ) {
      sectionQuestions.load(nav.selectedSection.Data.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav.selectedSection]);

  // "Move to another section" requested from the shared question dropdown.
  useEffect(() => {
    if (!sectionToMove || !sampleToMoveId) return;
    const identifierParts = sectionToMove.identifier.split('_');
    identifierParts[2] = sectionToMove.sectionCode.toUpperCase();
    const identifier = identifierParts.join('_');

    (async () => {
      try {
        await editor.moveQuestionToSection({
          questionId: sampleToMoveId,
          sectionCode: sectionToMove.sectionCode,
          identifier,
          surveyId: sectionToMove.surveyId,
        });
        nav.selectArea(WIZARD_AREAS.SECTIONS);
        handleSelectSection(sectionToMove.sectionCode.trim(), sectionToMove.sectionName.trim());
      } catch (e) {
        console.error(e);
      } finally {
        dispatch({ type: types.reducerTypes.UPDATED_SECTION_ON_QUESTION });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionToMove, sampleToMoveId]);

  // Reordering questions (shared dropdown) went through redux -> refresh list.
  useEffect(() => {
    if (!sampleMoved) return;
    sectionQuestions.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleMoved]);

  // The question sample/dimensions could not be loaded -> tell the user and
  // drop back to the section overview.
  useEffect(() => {
    if (!editor.loadError) return;
    nav.selectArea(WIZARD_AREAS.SECTIONS);
    SampleDimension404Dialog(dispatch);
    editor.clearLoadError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.loadError]);

  // A save is starting: collapse the expanded question panel so its chart is
  // torn down instead of lingering on the pre-save render, remembering which
  // one to reopen when the refreshed data is ready. previewRefreshing goes up
  // here, not when the request lands, so both panels hold the saving state
  // without a gap between the save request and the refresh.
  const handleQuestionSave = useCallback(
    async (payload) => {
      reopenQuestionIdRef.current = openedQuestionsRef.current?.[0] ?? null;
      dispatch({ type: types.reducerTypes.SURVEY_DETAILS_CLEAR_ALL_OPEN_QUESTIONS });
      setPreviewRefreshing(true);
      try {
        await editor.saveQuestion(payload);
      } catch (e) {
        setPreviewRefreshing(false);
        throw e;
      }
    },
    [dispatch, editor]
  );

  // The save landed: keep the loading state up, wait out the backend cache
  // rebuild, refetch the preview data, then reopen the question panel and
  // reload it. Loading only clears once that reload reports back
  // (handleQuestionRefreshed), so it stays continuous from save to fresh data.
  //
  // A question saved as an attribute filter is edited from Other Filters from
  // then on: skip reopening its chart panel and, once the refresh is done,
  // close the question editor. Until then the editor stays under the same
  // saving overlay as the preview, so no further edits get in.
  const handleQuestionSaved = useCallback(
    (saved) => {
      const savedAsAttribute = saved?.filterType === QUESTION_FILTER_TYPE.ATTRIBUTE;
      if (savedAsAttribute) {
        reopenQuestionIdRef.current = null;
      }
      setPreviewRefreshing(true);
      setTimeout(async () => {
        // Refetch the visualizations AND the section's questions: the question
        // list carries the chart data each preview falls back to, so without it
        // the chart keeps rendering the pre-save result set.
        const [, refreshedSection] = await Promise.all([
          data.refreshPreviewData(),
          sectionQuestions.reload({ silent: true }),
        ]);
        const reopenId = reopenQuestionIdRef.current;
        reopenQuestionIdRef.current = null;
        // Only a question that is still listed can reload and report back. A
        // question just turned into an ATTRIBUTE filter leaves the section list
        // (it moves under Other Filters), so waiting on it would leave the
        // loading overlays up forever.
        const stillListed = (refreshedSection?.questions || []).some(
          (question) =>
            question.id === reopenId && question.filterType !== QUESTION_FILTER_TYPE.ATTRIBUTE
        );
        if (reopenId && stillListed) {
          // Reopen the panel BEFORE asking it to reload: the reload effect only
          // runs for an expanded question, and clearing the loading state
          // depends on that reload reporting back.
          dispatch({
            type: types.reducerTypes.SURVEY_DETAILS_ONLY_OPEN_QUESTIONS,
            payload: { questionId: reopenId },
          });
          setQuestionRefreshToken((token) => token + 1);
        } else {
          // No expanded question left to reload -> nothing further to wait for.
          if (savedAsAttribute) nav.closeEditor();
          setPreviewRefreshing(false);
        }
      }, PREVIEW_REFRESH_DELAY_MS);
    },
    [data, dispatch, nav, sectionQuestions]
  );

  // Clicking a question opens the editor right away so both panels show the
  // loading state together, then loads the sample while SectionQuestion fetches
  // the chart detail and categories. Both panels stay loading until ALL of it
  // is in (handleSelectQuestion). Attribute questions are edited from Other
  // Filters: as soon as the sample shows one, close the editor and say so.
  const { loadQuestion } = editor;
  const handleLoadQuestion = useCallback(
    (questionId) => {
      openingQuestionIdRef.current = questionId;
      setOpeningQuestion(true);
      nav.openEditor(WIZARD_AREAS.QUESTION);
      questionLoadRef.current = loadQuestion(questionId).then((loaded) => {
        if (
          openingQuestionIdRef.current === questionId &&
          loaded?.sample?.Data.filterType === QUESTION_FILTER_TYPE.ATTRIBUTE
        ) {
          nav.closeEditor();
          AttributeQuestionDialog(dispatch);
        }
        return loaded;
      });
      return questionLoadRef.current;
    },
    [dispatch, loadQuestion, nav]
  );

  // SectionQuestion calls this once the chart detail and categories have
  // loaded (or failed); also wait for the sample so the form has its data.
  // Only the latest click clears the loading state, so a quick second click
  // keeps it up until its own data arrives.
  const handleSelectQuestion = useCallback(async (questionId) => {
    await questionLoadRef.current;
    if (openingQuestionIdRef.current === questionId) {
      openingQuestionIdRef.current = null;
      setOpeningQuestion(false);
    }
  }, []);

  // The post-save reload went through the click path (handleLoadQuestion), so
  // wait for its sample too and clear that opening state together with the
  // refresh: both panels leave "Saving your changes" at the same moment,
  // without switching to "Loading question" in between.
  const handleQuestionRefreshed = useCallback(async () => {
    await questionLoadRef.current;
    openingQuestionIdRef.current = null;
    setOpeningQuestion(false);
    setPreviewRefreshing(false);
  }, []);

  const handleSurveyInfoSave = useCallback(
    async (surveyInfo) => {
      setSavingSurveyInfo(true);
      try {
        await data.saveSurveyInfo(surveyInfo);
      } catch (e) {
        console.error(e);
      } finally {
        setSavingSurveyInfo(false);
      }
    },
    [data]
  );

  const handleSectionSave = useCallback(
    async (sectionInfo) => {
      setSavingSection(true);
      setSectionSaveError('');
      try {
        const { isNew, ...selectedSection } = nav.selectedSection;
        const oldCode = selectedSection.Data.code;
        const codeChanged = !isNew && oldCode.toLowerCase() !== sectionInfo.code.toLowerCase();

        // The questions carry the section code, so they move first; the
        // section entry follows so the new code keeps the section's name.
        let failedQuestions = [];
        if (codeChanged) {
          const { failed } = await recodeSectionQuestions({
            surveyId: currentSurvey.id,
            oldCode,
            newCode: sectionInfo.code,
            hasQuestions: (currentSurvey.surveySummaryDetailsDto?.sectionCodes || '')
              .split(',')
              .some((code) => code.trim().toLowerCase() === oldCode.toLowerCase()),
            jwt: data.jwt,
          });
          failedQuestions = failed;
        }

        const updatedSection = await saveSurveySection({
          sectionInfo,
          selectedSection,
          survey: currentSurvey,
          jwt: data.jwt,
        });
        if (codeChanged) {
          data.replaceSectionCode(oldCode, updatedSection.Data.code, {
            keepOldCode: failedQuestions.length > 0,
          });
        }
        const freshSections = await data.refetchSections();
        // A section entry created by this save only gets its Id from the
        // refetched list; without it the next save would create a duplicate.
        const savedEntry =
          updatedSection.Id === -1 && Array.isArray(freshSections)
            ? findSectionByCodeAndSurvey(updatedSection.Data.code, currentSurvey, freshSections)
            : undefined;
        nav.selectSection(
          savedEntry
            ? {
                ...savedEntry,
                storedCode: savedEntry.Data.code,
                Data: { ...savedEntry.Data, code: updatedSection.Data.code },
              }
            : updatedSection
        );
        if (codeChanged) data.refreshPreviewData();

        if (failedQuestions.length > 0) {
          setSectionSaveError(
            `${failedQuestions.length} question(s) could not be moved to ${
              updatedSection.Data.code
            } and are still under ${oldCode.toUpperCase()}. Move them with "Move to another section".`
          );
        } else if (isNew || codeChanged) {
          nav.closeEditor();
        }
      } catch (e) {
        console.error(e);
        setSectionSaveError('The section could not be saved. Please try again.');
      } finally {
        setSavingSection(false);
      }
    },
    [nav, currentSurvey, data]
  );

  // Clicking a filter row: collapse it if it is already selected, otherwise
  // expand it and, when a sample backs it, load it into the filter edit pane.
  const handleSelectFilter = useCallback(
    (filter) => {
      setFilterTagPreview(null);
      if (selectedFilter?.code === filter.code) {
        setSelectedFilter(null);
        nav.closeEditor();
        return;
      }
      setSelectedFilter(filter);
      const sampleId = getFilterSampleId(filter);
      if (sampleId === undefined) {
        nav.selectArea(WIZARD_AREAS.FILTERS);
        return;
      }
      nav.openEditor(WIZARD_AREAS.FILTERS);
      filterEditor.loadFilter(sampleId);
    },
    [selectedFilter, nav, filterEditor]
  );

  const handleCloseFilterEditor = useCallback(() => {
    setSelectedFilter(null);
    setFilterTagPreview(null);
    nav.closeEditor();
  }, [nav]);

  // The filter's sample/dimensions could not be loaded -> tell the user and
  // drop back to the filter overview.
  useEffect(() => {
    if (!filterEditor.loadError) return;
    setSelectedFilter(null);
    nav.selectArea(WIZARD_AREAS.FILTERS);
    FilterSample404Dialog(dispatch);
    filterEditor.clearLoadError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterEditor.loadError]);

  // Leaving the Other Filters area drops the row selection so coming back
  // starts from the collapsed overview.
  useEffect(() => {
    if (nav.area !== WIZARD_AREAS.FILTERS) {
      setSelectedFilter(null);
      setFilterTagPreview(null);
    }
  }, [nav.area]);

  const handleFilterSave = useCallback(
    (payload) => filterEditor.saveFilter(payload),
    [filterEditor]
  );

  // Same choreography as a question save: keep the loading state up, wait out
  // the backend cache rebuild, then refetch the filter list.
  const handleFilterSaved = useCallback(() => {
    setPreviewRefreshing(true);
    setTimeout(async () => {
      await data.refreshPreviewData();
      setPreviewRefreshing(false);
    }, PREVIEW_REFRESH_DELAY_MS);
  }, [data]);

  const sortSections = (a, b) => {
    if (a === b) return 0;
    return a < b ? -1 : 1;
  };

  const showSectionDescriptions = () => {
    return getSurveySectionCodes(currentSurvey, allSections)
      .sort(sortSections)
      .map((code) => {
        const selectedSectionCode = code.trim().toLowerCase();
        const sectionName = getSectionNameByCodeAndSurvey(
          selectedSectionCode,
          currentSurvey,
          allSections
        );
        const alertVariant =
          nav.selectedSection?.Data?.code !== selectedSectionCode
            ? 'alert-border'
            : 'alert-border-selected';
        return (
          <Alert
            key={code.trim()}
            variant="light"
            className={`alert-container ${alertVariant}`}
            onClick={() => handleSelectSection(code.trim(), sectionName.trim())}
          >
            {sectionName.trim()}
          </Alert>
        );
      });
  };

  const showStep1 = isStepVisible(ONBOARDING_STEPS.step1);
  const sectionAreaActive = [WIZARD_AREAS.SECTIONS, WIZARD_AREAS.QUESTION].includes(nav.area);
  const previewBusy =
    openingQuestion || editor.loading || editor.saving || sampleActionLoading || previewRefreshing;
  const savingQuestion = editor.saving || previewRefreshing;
  // The single loading condition both panels use, so the sidebar form and the
  // preview always start and finish loading together. It covers the question
  // save/refresh cycle, a section save (which re-selects and reloads the
  // section), and the section question list loading on its own.
  const rightPanelBusy =
    previewBusy || sectionQuestions.isLoading || savingSection || savingSurveyInfo;
  // The Other Filters panel and its edit pane load and save together, same as
  // the question editor and its preview.
  const filtersBusy = filterEditor.loading || filterEditor.saving || previewRefreshing;

  if (data.loading) {
    return (
      <Container fluid>
        <Row>
          <Col xs="12" className="d-flex justify-content-center mt-5">
            <Spinner animation="border" />
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="survey-wizard-container">
      <Row>
        {!nav.editing && (
          <Col md="3" className="pt-5 survey-sidebar p-0 pr-5">
            <div className="pt-3 pb-4 pl-5">
              <p>Survey</p>
              <h2>{currentSurvey?.title}</h2>
            </div>
            <Alert
              variant="light"
              className={`alert-container alert-border ${
                nav.area === WIZARD_AREAS.INFO ? 'section-selected' : ''
              }`}
              onClick={() => nav.selectArea(WIZARD_AREAS.INFO)}
            >
              <FontAwesomeIcon size="lg" className="mr-3" icon={faInfoCircle} />
              Survey information
            </Alert>
            <OverlayTrigger
              placement="right"
              defaultShow={showStep1}
              show={showStep1}
              overlay={(props) =>
                benTooltip({
                  ...props,
                  title: ONBOARDING_STEPS.step1.title,
                  content: ONBOARDING_STEPS.step1.content,
                  close: () => completeStep(ONBOARDING_STEPS.step1.value),
                })
              }
            >
              <Alert
                variant="light"
                className={`alert-container alert-border ${
                  sectionAreaActive ? 'section-selected' : ''
                }`}
                onClick={() => nav.selectArea(WIZARD_AREAS.SECTIONS)}
              >
                <FontAwesomeIcon size="lg" className="mr-3" icon={faFileAlt} />
                Sections
              </Alert>
            </OverlayTrigger>
            {nav.area === WIZARD_AREAS.SECTIONS && (
              <Collapse in={nav.area === WIZARD_AREAS.SECTIONS}>
                <Container className="pl-3 sidebar-section-container">
                  <Alert variant="light" className="alert-container" onClick={handleNewSection}>
                    <FontAwesomeIcon size="lg" className="mr-3" icon={faPlus} />
                    New Section
                  </Alert>
                  {showSectionDescriptions()}
                </Container>
              </Collapse>
            )}
            <Alert
              variant="light"
              className={`alert-container alert-border ${
                nav.area === WIZARD_AREAS.FILTERS ? 'section-selected' : ''
              }`}
              onClick={() => nav.selectArea(WIZARD_AREAS.FILTERS)}
            >
              <FontAwesomeIcon size="lg" className="mr-3" icon={faSlidersH} />
              Other Filters
            </Alert>
          </Col>
        )}
        {nav.editing && nav.area === WIZARD_AREAS.INFO && (
          <SurveyInformationEditForm
            currentSurvey={currentSurvey}
            metadataDescriptions={data.metadataDescriptions}
            busy={savingSurveyInfo || rightPanelBusy}
            busyLabel="Saving survey information"
            handleEditMode={nav.closeEditor}
            handleSave={handleSurveyInfoSave}
          />
        )}
        {nav.editing && nav.area === WIZARD_AREAS.SECTIONS && (
          <SectionEditForm
            key={nav.selectedSection?.Data.code}
            isNew={!!nav.selectedSection?.isNew}
            currentCode={nav.selectedSection?.Data.code}
            takenCodes={getSurveySectionCodes(currentSurvey, allSections).filter(
              (code) => code.toLowerCase() !== nav.selectedSection?.Data.code?.toLowerCase()
            )}
            saveError={sectionSaveError}
            currentSection={
              nav.selectedSection?.isNew
                ? ''
                : getSectionNameByCodeAndSurvey(
                    nav.selectedSection?.Data.code,
                    currentSurvey,
                    allSections
                  )
            }
            busy={rightPanelBusy}
            busyLabel="Saving section"
            handleEditMode={handleCloseSectionEditor}
            handleSave={handleSectionSave}
          />
        )}
        {nav.editing && nav.area === WIZARD_AREAS.FILTERS && (
          <FilterEditForm
            sample={filterEditor.sample}
            dimensions={filterEditor.dimensions}
            busy={filtersBusy}
            busyLabel={
              filterEditor.saving || previewRefreshing ? 'Saving your changes' : 'Loading filter'
            }
            onClose={handleCloseFilterEditor}
            onSave={handleFilterSave}
            onSaved={handleFilterSaved}
            onPreviewChange={setFilterTagPreview}
          />
        )}
        {nav.editing && nav.area === WIZARD_AREAS.QUESTION && (
          <QuestionEditForm
            key={editor.sample?.Id}
            sample={editor.sample}
            dimensions={editor.dimensions}
            visualizations={data.visualizations}
            surveyLocationFilter={data.surveyLocationFilter}
            busy={rightPanelBusy}
            busyLabel={savingQuestion ? 'Saving your changes' : 'Loading question'}
            onClose={nav.closeEditor}
            onSave={handleQuestionSave}
            onSaved={handleQuestionSaved}
            mirrorToStore={editor.mirrorToStore}
          />
        )}
        <Col
          md="9"
          className={clsx(['survey-sections-container pt-5', { 'empty-section': !nav.area }])}
        >
          {!nav.area && (
            <>
              <img src="/empty-section.svg" alt="select a section" />
              <h2>Choose a Section to edit</h2>
            </>
          )}
          {nav.area === WIZARD_AREAS.INFO && (
            <SurveyInformationCard
              busy={savingSurveyInfo || rightPanelBusy}
              currentSurvey={currentSurvey}
              metadataDescriptions={data.metadataDescriptions}
              onEditModeClick={() => nav.openEditor(WIZARD_AREAS.INFO)}
            />
          )}
          {nav.selectedSection && sectionAreaActive && (
            <SectionQuestions
              sectionName={
                nav.selectedSection.isNew
                  ? 'New Section'
                  : getSectionNameByCodeAndSurvey(
                      nav.selectedSection.Data.code,
                      currentSurvey,
                      allSections
                    )
              }
              sectionCode={nav.selectedSection.Data.code}
              surveyQuestions={sectionQuestions.surveyQuestions}
              visualizations={data.visualizations}
              busy={rightPanelBusy}
              busyLabel={
                openingQuestion && !savingQuestion
                  ? 'Loading question'
                  : 'Updating preview with your changes'
              }
              isEditSection={nav.editing && nav.area === WIZARD_AREAS.SECTIONS}
              refreshToken={questionRefreshToken}
              onEditSectionClick={() => {
                setSectionSaveError('');
                nav.openEditor(WIZARD_AREAS.SECTIONS);
              }}
              onSelectQuestion={handleSelectQuestion}
              onLoadQuestion={handleLoadQuestion}
              onQuestionRefreshed={handleQuestionRefreshed}
            />
          )}
          {nav.area === WIZARD_AREAS.FILTERS && (
            <OtherFilters
              filters={data.surveyDetailFilters}
              busy={filtersBusy}
              selectedFilterCode={selectedFilter?.code}
              selectedFilterTags={filterTagPreview}
              onSelectFilter={handleSelectFilter}
            />
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default SurveyEditWizard;
